import React, { useState } from 'react'
import styled from 'styled-components'
import axios from 'axios'
import * as constants from '../../globals/constants'
import colors from '../../styles/colors'

const PERIOD_MONTH = 'month'
const PERIOD_QUARTER = 'quarter'

const CHANNEL_NAME_MAP = {
  HAPPYORDER: '해피오더',
  BAEMIN: '배달의민족',
  BAERA: '배민1',
  YGY: '요기요',
  YGYD: '요기요델리',
  YGYE: '요기배달',
  COUPANG: '쿠팡이츠',
  KAKAO: '카카오 주문하기',
  KAKAOR: '카카오 예약',
  KAKAOG: '카카오 선물하기',
  NAVER: '네이버',
  NAVERSO: '네이버 스마트주문',
  DDANGYO: '땡겨요',
  PASSORDER: '패스오더',
  HOQR: 'HOQR',
  BRAPP: 'BR앱',
  HDINCAR: '현대In-Car',
  CSCALL: '전화주문(CS)',
  DAANGN: '당근',
  KNOWK: '노크',
  OWIN: '오윈',
  TMAP: '티맵주문',
}

const YEARS = Array.from({ length: 10 }, (_, i) => 2025 + i)
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1)
const QUARTERS = [
  { value: 1, label: '1분기' },
  { value: 2, label: '2분기' },
  { value: 3, label: '3분기' },
  { value: 4, label: '4분기' },
]

const getQuarterRange = (quarter) => ({
  start: (quarter - 1) * 3 + 1,
  end: quarter * 3,
})

const toNumberSafe = (value) => {
  if (value === null || value === undefined || value === '') return 0
  const n = Number(value)
  return isNaN(n) ? 0 : n
}

const formatMoney = (n) => {
  if (n === null || n === undefined || n === '') return '-'
  return Number(n).toLocaleString()
}

const getChannelName = (code) => {
  if (!code) return '-'
  return CHANNEL_NAME_MAP[code] || code
}

export default function VatReport() {
  const user = JSON.parse(localStorage.getItem('user'))

  const [periodType, setPeriodType] = useState(PERIOD_MONTH)
  const [searchYear, setSearchYear] = useState(
    String(new Date().getFullYear()),
  )
  const [searchMonth, setSearchMonth] = useState('')
  const [searchQuarter, setSearchQuarter] = useState('1')

  const [summaryRows, setSummaryRows] = useState([])
  const [totalRow, setTotalRow] = useState(null)
  const [periodDisplayText, setPeriodDisplayText] = useState('')

  const [isSearching, setIsSearching] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [searchDone, setSearchDone] = useState(false)

  const [lastSearch, setLastSearch] = useState({
    periodType: '',
    year: '',
    month: '',
    quarter: '',
  })

  const buildPeriodDisplayText = (type, year, month, quarter) => {
    if (type === PERIOD_MONTH) {
      return `${year}년 ${month}월`
    }
    const range = getQuarterRange(Number(quarter))
    return `${year}년 ${range.start}월 ~ ${range.end}월`
  }

  const getQuarterMonthRangeText = () => {
    const q = Number(searchQuarter)
    if (isNaN(q) || q < 1 || q > 4) return ''
    const range = getQuarterRange(q)
    return `(${range.start}월~${range.end}월)`
  }

  const mapRows = (resultData) => {
    const list = Array.isArray(resultData)
      ? resultData
      : resultData?.vatChannelSummaryList || []
    if (!list.length) return []

    return list
      .filter((raw) => !!raw)
      .map((raw) => {
        const card = toNumberSafe(raw.cardPrice)
        const cash = toNumberSafe(raw.cashPrice)
        const etc = toNumberSafe(raw.etcPrice)
        return {
          orderChannelCd: raw.orderChannelCd || '',
          orderChannelName: getChannelName(raw.orderChannelCd),
          cardPrice: card,
          cashPrice: cash,
          etcPrice: etc,
          totalPrice: card + cash + etc,
        }
      })
  }

  const buildTotalRow = (rows) => {
    const total = {
      orderChannelName: '합계',
      cardPrice: 0,
      cashPrice: 0,
      etcPrice: 0,
      totalPrice: 0,
    }
    rows.forEach((r) => {
      total.cardPrice += r.cardPrice
      total.cashPrice += r.cashPrice
      total.etcPrice += r.etcPrice
      total.totalPrice += r.totalPrice
    })
    return total
  }

  const getSearchIdentity = () => ({
    searchBrand: user?.brandCode || '',
    searchStore: user?.storeCode || '',
  })

  const handleSearch = async () => {
    if (isSearching) return

    if (!searchYear) {
      alert('연도를 선택해주세요.')
      return
    }
    if (periodType === PERIOD_MONTH && !searchMonth) {
      alert('월을 선택해주세요.')
      return
    }
    if (periodType === PERIOD_QUARTER && !searchQuarter) {
      alert('분기를 선택해주세요.')
      return
    }

    const identity = getSearchIdentity()
    const isMonth = periodType === PERIOD_MONTH
    const url = isMonth
      ? constants.API.VAT_MONTH_SUMMARY
      : constants.API.VAT_QUARTER_SUMMARY

    const body = {
      year: Number(searchYear),
      searchBrand: identity.searchBrand,
      searchStore: identity.searchStore,
      ...(isMonth
        ? { month: Number(searchMonth) }
        : { quarter: Number(searchQuarter) }),
    }

    setIsSearching(true)
    try {
      const res = await axios.post(url, body, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
        params: {
          userId: user?.userId?.replace(/[^a-zA-Z0-9]/g, '') || '',
        },
      })

      const d = res.data
      if (typeof d !== 'object' || d === null) {
        alert('서버 응답이 올바르지 않습니다. 관리자에게 문의해 주세요.')
        return
      }
      if (d.resultCode !== '0000') {
        alert(d.resultMsg || d.resultMessage || '조회에 실패했습니다.')
        return
      }

      const resultData = d.resultData || []
      const rows = mapRows(resultData)

      setSummaryRows(rows)
      setTotalRow(buildTotalRow(rows))
      setSearchDone(true)

      const newLast = {
        periodType,
        year: String(searchYear),
        month: isMonth ? searchMonth : '',
        quarter: !isMonth ? searchQuarter : '',
      }
      setLastSearch(newLast)
      setPeriodDisplayText(
        buildPeriodDisplayText(
          periodType,
          searchYear,
          isMonth ? searchMonth : '',
          !isMonth ? searchQuarter : '',
        ),
      )
    } catch (err) {
      const data = err?.response?.data
      const serverMsg =
        (typeof data === 'object' && (data?.resultMessage || data?.resultMsg)) ||
        ''
      alert(serverMsg || '조회 중 오류가 발생했습니다.')
    } finally {
      setIsSearching(false)
    }
  }

  const parseBlobErrorMessage = async (blob) => {
    try {
      const text = await blob.text()
      const json = JSON.parse(text)
      return json?.resultMessage || json?.resultMsg || ''
    } catch (e) {
      return ''
    }
  }

  const extractFileNameFromHeader = (disposition) => {
    if (!disposition) return ''
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''))
      } catch (e) {
        return ''
      }
    }
    const plainMatch = disposition.match(/filename="?([^";]+)"?/i)
    if (plainMatch && plainMatch[1]) {
      return plainMatch[1].trim()
    }
    return ''
  }

  const triggerBlobDownload = (blob, fileName) => {
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(objectUrl)
  }

  const handleDownload = async () => {
    if (!lastSearch.periodType) {
      alert('먼저 검색해주세요.')
      return
    }
    if (isDownloading) return

    const identity = getSearchIdentity()
    const isMonth = lastSearch.periodType === PERIOD_MONTH

    const params = {
      year: lastSearch.year,
      searchBrand: identity.searchBrand,
      searchStore: identity.searchStore,
      userId: user?.userId?.replace(/[^a-zA-Z0-9]/g, '') || '',
      ...(isMonth
        ? { month: lastSearch.month }
        : { quarter: lastSearch.quarter }),
    }

    const url = isMonth
      ? constants.API.VAT_MONTH_EXCEL_DOWNLOAD
      : constants.API.VAT_QUARTER_EXCEL_DOWNLOAD

    const suffix = isMonth
      ? `${lastSearch.month}월`
      : `${lastSearch.quarter}분기`
    const fallbackFileName = `부가세신고자료_${lastSearch.year}_${suffix}.xlsx`

    setIsDownloading(true)
    try {
      const res = await axios({
        method: 'GET',
        url,
        params,
        responseType: 'blob',
        withCredentials: true,
      })

      const contentType =
        res.headers?.['content-type'] || res.headers?.['Content-Type'] || ''

      if (contentType.includes('application/json')) {
        const message = await parseBlobErrorMessage(res.data)
        alert(message || '엑셀 다운로드에 실패했습니다.')
        return
      }

      const serverFileName = extractFileNameFromHeader(
        res.headers?.['content-disposition'] ||
          res.headers?.['Content-Disposition'],
      )
      const fileName = serverFileName || fallbackFileName

      triggerBlobDownload(
        new Blob([res.data], {
          type:
            contentType ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        fileName,
      )
    } catch (err) {
      const errorData = err?.response?.data
      let message = ''
      if (errorData instanceof Blob) {
        message = await parseBlobErrorMessage(errorData)
      } else if (typeof errorData === 'object' && errorData !== null) {
        message = errorData.resultMessage || errorData.resultMsg || ''
      }
      alert(message || '엑셀 다운로드 중 오류가 발생했습니다.')
    } finally {
      setIsDownloading(false)
    }
  }

  const hasData = searchDone && summaryRows.length > 0

  return (
    <Container>
      <Title>해피오더 부가세 신고자료</Title>

      <FilterSection>
        <FilterRow>
          <RadioWrapper>
            <RadioInput
              type="radio"
              id="periodMonth"
              name="periodType"
              checked={periodType === PERIOD_MONTH}
              onChange={() => setPeriodType(PERIOD_MONTH)}
            />
            <RadioLabel htmlFor="periodMonth">월</RadioLabel>
          </RadioWrapper>

          <Select
            value={searchYear}
            onChange={(e) => setSearchYear(e.target.value)}
          >
            {YEARS.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </Select>

          <Select
            value={searchMonth}
            onChange={(e) => setSearchMonth(e.target.value)}
            disabled={periodType !== PERIOD_MONTH}
          >
            <option value="">월</option>
            {MONTHS.map((m) => (
              <option key={m} value={String(m)}>
                {m}월
              </option>
            ))}
          </Select>

          {periodType === PERIOD_MONTH && (
            <SearchButton onClick={handleSearch} disabled={isSearching}>
              {isSearching ? '검색중' : '검색'}
            </SearchButton>
          )}
        </FilterRow>

        <FilterRow>
          <RadioWrapper>
            <RadioInput
              type="radio"
              id="periodQuarter"
              name="periodType"
              checked={periodType === PERIOD_QUARTER}
              onChange={() => setPeriodType(PERIOD_QUARTER)}
            />
            <RadioLabel htmlFor="periodQuarter">분기</RadioLabel>
          </RadioWrapper>

          <Select
            value={searchYear}
            onChange={(e) => setSearchYear(e.target.value)}
          >
            {YEARS.map((y) => (
              <option key={`q${y}`} value={String(y)}>
                {y}
              </option>
            ))}
          </Select>

          <Select
            value={searchQuarter}
            onChange={(e) => setSearchQuarter(e.target.value)}
            disabled={periodType !== PERIOD_QUARTER}
          >
            {QUARTERS.map((q) => (
              <option key={q.value} value={String(q.value)}>
                {q.label}
              </option>
            ))}
          </Select>

          {periodType === PERIOD_QUARTER && (
            <>
              <QuarterRangeText>{getQuarterMonthRangeText()}</QuarterRangeText>
              <SearchButton onClick={handleSearch} disabled={isSearching}>
                {isSearching ? '검색중' : '검색'}
              </SearchButton>
            </>
          )}
        </FilterRow>
      </FilterSection>

      {hasData && (
        <PeriodBar>
          <PeriodText>조회 기간 : {periodDisplayText}</PeriodText>
          <DownloadLink onClick={handleDownload} disabled={isDownloading}>
            &#x1F4E5; 다운로드
          </DownloadLink>
        </PeriodBar>
      )}

      {!searchDone ? (
        <EmptyMessage>검색 조건을 선택해 주세요.</EmptyMessage>
      ) : summaryRows.length === 0 ? (
        <EmptyMessage>조회된 데이터가 없습니다.</EmptyMessage>
      ) : (
        <TableWrapper>
          <DataTable>
            <thead>
              <tr>
                <Th>주문채널</Th>
                <Th>카드</Th>
                <Th>현금성</Th>
                <Th>기타</Th>
                <Th>총합계</Th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((row, i) => (
                <tr key={i}>
                  <Td>{row.orderChannelName}</Td>
                  <TdRight>{formatMoney(row.cardPrice)}</TdRight>
                  <TdRight>{formatMoney(row.cashPrice)}</TdRight>
                  <TdRight>{formatMoney(row.etcPrice)}</TdRight>
                  <TdRight>{formatMoney(row.totalPrice)}</TdRight>
                </tr>
              ))}
              {totalRow && (
                <TotalTr>
                  <TdTotal>{totalRow.orderChannelName}</TdTotal>
                  <TdTotalRight>{formatMoney(totalRow.cardPrice)}</TdTotalRight>
                  <TdTotalRight>
                    {formatMoney(totalRow.cashPrice)}
                  </TdTotalRight>
                  <TdTotalRight>{formatMoney(totalRow.etcPrice)}</TdTotalRight>
                  <TdTotalRight>
                    {formatMoney(totalRow.totalPrice)}
                  </TdTotalRight>
                </TotalTr>
              )}
            </tbody>
          </DataTable>
        </TableWrapper>
      )}

      <Legend>
        <LegendItem>
          <strong>카드</strong> : 카드로 결제된 매출
        </LegendItem>
        <LegendItem>
          <strong>현금성</strong> : 대표자의 명의로 발행된 현금영수증
          매출(모바일상품권 포함)
        </LegendItem>
        <LegendItem>
          <strong>기타</strong> : 포인트, 쿠폰 등 카드 및 현금매출에 해당하지
          않는 매출
        </LegendItem>
      </Legend>

      <NoticeSection>
        <NoticeItem>• 채널 부가세 신고를 위한 참고용 자료입니다.</NoticeItem>
        <NoticeItem>• 2026년 1월 데이터부터 조회 가능합니다.</NoticeItem>
        <NoticeItem>• 지난달 데이터는 이번 달 5영업일 내 조회 가능합니다.</NoticeItem>
      </NoticeSection>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
  align-items: stretch;

  @media ${(props) => props.theme.mobile} {
    padding: 0 16px;
  }
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.BK};
  margin-bottom: 24px;
  text-align: center;
`

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
  padding: 16px;
  background-color: ${colors.W2};
  border-radius: 8px;
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const RadioWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 48px;
`

const RadioInput = styled.input`
  accent-color: ${colors.mainG};
  width: 16px;
  height: 16px;
  cursor: pointer;
`

const RadioLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.BK};
  cursor: pointer;
  white-space: nowrap;
`

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${colors.G5};
  border-radius: 6px;
  font-size: 14px;
  color: ${colors.BK};
  background-color: ${colors.W1};
  outline: none;
  cursor: pointer;
  min-width: 80px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    border-color: ${colors.mainG};
  }
`

const SearchButton = styled.button`
  height: 36px;
  padding: 0 20px;
  background-color: ${colors.mainG};
  color: ${colors.W1};
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const QuarterRangeText = styled.span`
  font-size: 13px;
  color: #e74c3c;
  font-weight: 600;
  white-space: nowrap;
`

const PeriodBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 4px;
`

const PeriodText = styled.span`
  font-size: 13px;
  color: ${colors.G4};
`

const DownloadLink = styled.button`
  background: none;
  border: none;
  font-size: 13px;
  color: ${colors.G4};
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;

  &:hover {
    color: ${colors.mainG};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const EmptyMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  font-size: 14px;
  color: ${colors.G4};
  background-color: ${colors.W2};
  border-radius: 8px;
  margin-bottom: 20px;
`

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.G5};
`

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 400px;
`

const Th = styled.th`
  background-color: ${colors.mainG};
  color: ${colors.W1};
  font-size: 13px;
  font-weight: 700;
  padding: 10px 8px;
  text-align: center;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }
`

const Td = styled.td`
  font-size: 13px;
  padding: 10px 8px;
  text-align: center;
  border-bottom: 1px solid ${colors.G3};
  border-right: 1px solid ${colors.G3};
  color: ${colors.BK};

  &:last-child {
    border-right: none;
  }
`

const TdRight = styled(Td)`
  text-align: right;
`

const TotalTr = styled.tr`
  background-color: ${colors.W2};
`

const TdTotal = styled(Td)`
  font-weight: 700;
  border-bottom: none;
`

const TdTotalRight = styled(TdRight)`
  font-weight: 700;
  border-bottom: none;
`

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;
  padding: 0 4px;
`

const LegendItem = styled.span`
  font-size: 12px;
  color: ${colors.G4};
  line-height: 1.6;
`

const NoticeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  background-color: ${colors.W2};
  border-radius: 8px;
  margin-bottom: 40px;
`

const NoticeItem = styled.span`
  font-size: 12px;
  color: ${colors.G4};
  line-height: 1.5;
`
